import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, Timestamp, query, orderBy } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import DancerThumbnail from './DancerThumbnail';
import { resolveStudioName } from './utils/studioNameResolver';
import './StudioTransfers.css';

// Competition History Entry with Studio Editor
function CompetitionHistoryEntry({ entry, dancerName, formatDate, allStudios, onStudioChange, isEditing, onEditToggle, pendingStudio }) {
  const [resolvedName, setResolvedName] = useState(entry.studio);
  const [selectedStudio, setSelectedStudio] = useState(pendingStudio || entry.studio);
  const [studioSearch, setStudioSearch] = useState('');
  const [filteredStudios, setFilteredStudios] = useState([]);
  
  useEffect(() => {
    resolveStudioName(entry.studio, dancerName).then(setResolvedName);
  }, [entry.studio, dancerName]);

  useEffect(() => {
    setSelectedStudio(pendingStudio || entry.studio);
  }, [pendingStudio, entry.studio]);

  useEffect(() => {
    if (studioSearch) {
      const filtered = allStudios.filter(studio => 
        studio.toLowerCase().includes(studioSearch.toLowerCase())
      );
      setFilteredStudios(filtered);
    } else {
      setFilteredStudios([]);
    }
  }, [studioSearch, allStudios]);

  const handleStudioChange = (newStudio) => {
    setSelectedStudio(newStudio);
    onStudioChange(entry, newStudio);
  };

  const isResolved = resolvedName !== entry.studio;
  const hasChanges = selectedStudio !== entry.studio;
  
  return (
    <div className={`timeline-entry competition-history-entry ${hasChanges ? 'has-changes' : ''}`}>
      <div className="timeline-date">{formatDate(entry.date)}</div>
      <div className="timeline-content">
        {!isEditing ? (
          <>
            <div className="studio-display">
              <strong className={isResolved ? 'resolved-studio' : ''}>
                {pendingStudio && pendingStudio !== entry.studio ? (
                  <>
                    <span className="pending-studio">{pendingStudio}</span>
                    <small className="original-studio"> (was: {resolvedName})</small>
                  </>
                ) : (
                  resolvedName
                )}
              </strong>
              {isResolved && !pendingStudio && (
                <div className="original-studio">
                  <small>Originally: {entry.studio}</small>
                </div>
              )}
              <button 
                className="edit-studio-btn"
                onClick={onEditToggle}
                title="Edit studio for this competition"
              >
                ✏️ Edit
              </button>
            </div>
          </>
        ) : (
          <div className="studio-editor">
            <div className="studio-selection-dropdown">
              <label>Select Studio:</label>
              <select 
                value={selectedStudio} 
                onChange={(e) => handleStudioChange(e.target.value)}
                className="studio-select"
              >
                <option value="">No Studio</option>
                {entry.dancerStudios && entry.dancerStudios.map((studio, idx) => (
                  <option key={idx} value={studio}>{studio}</option>
                ))}
              </select>
            </div>
            
            <div className="studio-search-section">
              <label>Or search all studios:</label>
              <input
                type="text"
                placeholder="Search studios..."
                value={studioSearch}
                onChange={(e) => setStudioSearch(e.target.value)}
                className="studio-search-input"
              />
              {filteredStudios.length > 0 && (
                <div className="studio-search-results">
                  {filteredStudios.slice(0, 10).map((studio, idx) => (
                    <button
                      key={idx}
                      className="studio-search-result"
                      onClick={() => {
                        handleStudioChange(studio);
                        setStudioSearch('');
                        setFilteredStudios([]);
                      }}
                    >
                      {studio}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="studio-edit-actions">
              <button onClick={onEditToggle} className="done-editing-btn">
                ✅ Done
              </button>
            </div>
          </div>
        )}
        <br />
        <small><strong>{entry.competition}</strong></small>
        <br />
        <small>Competition ID: {entry.competitionId}</small>
      </div>
    </div>
  );
}

// Timeline entry component that shows resolved studio names
function StudioTimelineEntry({ entry, dancerName, formatDate }) {
  const [resolvedName, setResolvedName] = useState(entry.studio);
  
  useEffect(() => {
    resolveStudioName(entry.studio, dancerName).then(setResolvedName);
  }, [entry.studio, dancerName]);
  
  const isResolved = resolvedName !== entry.studio;
  
  return (
    <div className="timeline-entry">
      <div className="timeline-date">{formatDate(entry.date)}</div>
      <div className="timeline-content">
        <strong className={isResolved ? 'resolved-studio' : ''}>{resolvedName}</strong>
        {isResolved && (
          <div className="original-studio">
            <small>Originally: {entry.studio}</small>
          </div>
        )}
        <br />
        <small>{entry.competition}</small>
      </div>
    </div>
  );
}

// Modal for setting up studio collaborations
function CollaborationModal({ dancer, studios, onSave, onClose }) {
  const [selectedStudios, setSelectedStudios] = useState([]);
  const [disciplines, setDisciplines] = useState('');

  const toggleStudio = (studio) => {
    setSelectedStudios(prev => {
      if (prev.includes(studio)) {
        return prev.filter(s => s !== studio);
      } else {
        return [...prev, studio];
      }
    });
  };

  const handleSave = () => {
    if (selectedStudios.length < 2) {
      alert('Please select at least 2 studios for collaboration');
      return;
    }
    onSave(selectedStudios, disciplines);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>🤝 Set Studio Collaboration for {dancer?.name}</h3>
        <p>Select multiple studios that collaborate for this dancer:</p>
        
        <div className="studio-selection">
          {studios.map((studio, idx) => (
            <button
              key={idx}
              className={`studio-toggle ${selectedStudios.includes(studio) ? 'selected' : ''}`}
              onClick={() => toggleStudio(studio)}
            >
              {studio} {selectedStudios.includes(studio) && '✓'}
            </button>
          ))}
        </div>

        <div className="form-row">
          <label>Disciplines (optional):</label>
          <input
            type="text"
            value={disciplines}
            onChange={(e) => setDisciplines(e.target.value)}
            placeholder="e.g., Latin, Contemporary, Jazz..."
          />
        </div>

        <div className="modal-actions">
          <button onClick={handleSave} className="save-btn">
            Save Collaboration
          </button>
          <button onClick={onClose} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal for setting discipline-specific studios
function DisciplineStudioModal({ dancer, studios, existingMappings, onSave, onClose }) {
  const [mappings, setMappings] = useState(existingMappings || {});
  const [newDiscipline, setNewDiscipline] = useState('');
  const [newStudio, setNewStudio] = useState('');

  const commonDisciplines = ['Latin', 'Standard', 'Contemporary', 'Jazz', 'Hip Hop', 'Commercial', 'Ballet', 'Lyrical', 'Tap'];

  const addMapping = () => {
    if (!newDiscipline || !newStudio) {
      alert('Please enter both discipline and studio');
      return;
    }
    setMappings(prev => ({ ...prev, [newDiscipline]: newStudio }));
    setNewDiscipline('');
    setNewStudio('');
  };

  const removeMapping = (discipline) => {
    setMappings(prev => {
      const updated = { ...prev };
      delete updated[discipline];
      return updated;
    });
  };

  const handleSave = () => {
    if (Object.keys(mappings).length === 0) {
      alert('Please add at least one discipline-studio mapping');
      return;
    }
    onSave(mappings);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>🎭 Set Discipline-Specific Studios for {dancer?.name}</h3>
        <p>Assign different studios for different dance disciplines:</p>
        
        <div className="current-mappings">
          <h4>Current Mappings:</h4>
          {Object.entries(mappings).map(([discipline, studio]) => (
            <div key={discipline} className="mapping-item">
              <span><strong>{discipline}:</strong> {studio}</span>
              <button 
                onClick={() => removeMapping(discipline)}
                className="remove-btn"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="add-mapping">
          <h4>Add New Mapping:</h4>
          <div className="form-row">
            <label>Discipline:</label>
            <select
              value={newDiscipline}
              onChange={(e) => setNewDiscipline(e.target.value)}
            >
              <option value="">Select or type discipline...</option>
              {commonDisciplines.map(disc => (
                <option key={disc} value={disc}>{disc}</option>
              ))}
            </select>
            <input
              type="text"
              value={newDiscipline}
              onChange={(e) => setNewDiscipline(e.target.value)}
              placeholder="Or type custom discipline..."
            />
          </div>
          
          <div className="form-row">
            <label>Studio:</label>
            <select
              value={newStudio}
              onChange={(e) => setNewStudio(e.target.value)}
            >
              <option value="">Select studio...</option>
              {studios.map((studio, idx) => (
                <option key={idx} value={studio}>{studio}</option>
              ))}
            </select>
          </div>
          
          <button onClick={addMapping} className="add-btn">
            Add Mapping
          </button>
        </div>

        <div className="modal-actions">
          <button onClick={handleSave} className="save-btn">
            Save Discipline Studios
          </button>
          <button onClick={onClose} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StudioTransfers() {
  const [loading, setLoading] = useState(true);
  const [dancers, setDancers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [selectedDancer, setSelectedDancer] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);
  const [primaryStudio, setPrimaryStudio] = useState('');
  const [newTransfer, setNewTransfer] = useState({
    fromStudio: '',
    toStudio: '',
    transferDate: '',
    notes: ''
  });
  const [studioCollaboration, setStudioCollaborationState] = useState({
    studios: [],
    disciplines: '',
    isActive: false
  });
  const [disciplineStudios, setDisciplineStudios] = useState({});
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);
  const [showDisciplineModal, setShowDisciplineModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [allStudios, setAllStudios] = useState([]);
  const [studioSearchTerm, setStudioSearchTerm] = useState('');
  const [showStudioDropdown, setShowStudioDropdown] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null); // Track which competition is being edited
  const [competitionData, setCompetitionData] = useState(new Map()); // Store competition data for editing
  const [pendingChanges, setPendingChanges] = useState(new Map()); // Track pending studio changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [bulkChangeStudio, setBulkChangeStudio] = useState(''); // For bulk studio change
  const [showBulkChange, setShowBulkChange] = useState(false);

  // Auth state management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDancersWithMultipleStudios(),
        loadTransferHistory(),
        loadAllStudios()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setStatusMessage('❌ Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllStudios = async () => {
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      const studioSet = new Set();
      
      competitionsSnapshot.forEach((doc) => {
        const competition = doc.data();
        if (competition.results) {
          competition.results.forEach(result => {
            if (result.studio && result.studio.trim() !== '') {
              studioSet.add(result.studio.trim());
            }
          });
        }
      });
      
      // Also add studios from studio profiles
      const studioProfilesSnapshot = await getDocs(collection(db, 'studioProfiles'));
      studioProfilesSnapshot.forEach((doc) => {
        const profile = doc.data();
        if (profile.name) {
          studioSet.add(profile.name);
        }
        if (profile.displayName) {
          studioSet.add(profile.displayName);
        }
      });
      
      setAllStudios(Array.from(studioSet).sort());
    } catch (error) {
      console.error('Error loading all studios:', error);
    }
  };

  const loadDancersWithMultipleStudios = async () => {
    try {
      // Get all competitions to analyze studio representations
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      const dancerStudioMap = new Map();
      const compData = new Map();

      competitionsSnapshot.forEach((doc) => {
        const competition = doc.data();
        const competitionId = doc.id;
        const results = competition.results || [];
        const competitionDate = competition.timestamp instanceof Timestamp 
          ? competition.timestamp.toDate() 
          : new Date(competition.timestamp);

        // Store competition data for editing
        compData.set(competitionId, {
          id: competitionId,
          name: competition.name || 'Unknown Competition',
          date: competitionDate,
          results: results
        });

        results.forEach((result) => {
          const dancerName = result.name || result.dancerName;
          const studio = result.studio || 'No Club';
          
          if (dancerName) {
            if (!dancerStudioMap.has(dancerName)) {
              dancerStudioMap.set(dancerName, []);
            }
            
            const dancerHistory = dancerStudioMap.get(dancerName);
            const existingEntry = dancerHistory.find(entry => 
              entry.studio === studio && 
              entry.competitionId === competitionId
            );
            
            if (!existingEntry) {
              dancerHistory.push({
                studio,
                date: competitionDate,
                competition: competition.name || 'Unknown Competition',
                competitionId: competitionId
              });
            }
          }
        });
      });

      // Store competition data for editing purposes
      setCompetitionData(compData);

      // Find dancers with multiple studios
      const dancersWithMultipleStudios = [];
      dancerStudioMap.forEach((history, dancerName) => {
        // Sort by date
        history.sort((a, b) => a.date - b.date);
        
        // Get unique studios for this dancer
        const uniqueStudios = [...new Set(history.map(h => h.studio))];
        
        if (uniqueStudios.length > 1) {
          // Add dancer studios to each history entry for the dropdown
          const enrichedHistory = history.map(entry => ({
            ...entry,
            dancerStudios: uniqueStudios
          }));

          dancersWithMultipleStudios.push({
            name: dancerName,
            studios: uniqueStudios,
            history: enrichedHistory,
            transferCount: uniqueStudios.length - 1
          });
        }
      });

      // Sort by transfer count (most transfers first)
      dancersWithMultipleStudios.sort((a, b) => b.transferCount - a.transferCount);
      
      setDancers(dancersWithMultipleStudios);
    } catch (error) {
      console.error('Error loading dancers:', error);
    }
  };

  const loadTransferHistory = async () => {
    try {
      const transfersSnapshot = await getDocs(
        query(collection(db, 'studioTransfers'), orderBy('timestamp', 'desc'))
      );
      const transfersList = transfersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTransfers(transfersList);
    } catch (error) {
      console.error('Error loading transfer history:', error);
    }
  };

  const selectDancer = async (dancer) => {
    setSelectedDancer(dancer);
    setTransferHistory(dancer.history);
    
    // Clear any pending changes when selecting a new dancer
    setPendingChanges(new Map());
    setHasUnsavedChanges(false);
    setEditingCompetition(null);
    
    // Clear bulk change state
    setShowBulkChange(false);
    setBulkChangeStudio('');
    
    // Load existing primary studio preference and discipline studios
    try {
      const profileSnapshot = await getDocs(collection(db, 'dancerProfiles'));
      const profile = profileSnapshot.docs.find(doc => doc.id === dancer.name);
      const profileData = profile?.data();
      
      if (profileData?.primaryStudio) {
        setPrimaryStudio(profileData.primaryStudio);
      } else {
        setPrimaryStudio('');
      }
      
      // Load discipline-specific studios
      if (profileData?.disciplineStudios) {
        setDisciplineStudios(profileData.disciplineStudios);
      } else {
        setDisciplineStudios({});
      }
      
      // Load studio collaboration
      if (profileData?.studioCollaboration) {
        setStudioCollaborationState({
          studios: profileData.studioCollaboration.studios || [],
          disciplines: profileData.studioCollaboration.disciplines || '',
          isActive: profileData.studioCollaboration.isActive || false
        });
      } else {
        setStudioCollaborationState({ studios: [], disciplines: '', isActive: false });
      }
    } catch (error) {
      console.error('Error loading dancer profile data:', error);
      setPrimaryStudio('');
      setDisciplineStudios({});
      setStudioCollaborationState({ studios: [], disciplines: '', isActive: false });
    }
    
    // Pre-fill transfer form if there's a clear last studio
    if (dancer.history.length > 0) {
      const lastEntry = dancer.history[dancer.history.length - 1];
      setNewTransfer(prev => ({
        ...prev,
        fromStudio: lastEntry.studio
      }));
    }
  };

  const setPrimaryStudioForDancer = async (studioName) => {
    if (!selectedDancer) return;

    try {
      const profileRef = doc(db, 'dancerProfiles', selectedDancer.name);
      await setDoc(profileRef, {
        name: selectedDancer.name,
        primaryStudio: studioName,
        primaryStudioSetBy: user?.email || 'Unknown',
        primaryStudioTimestamp: Timestamp.now(),
        lastUpdated: Timestamp.now()
      }, { merge: true });

      setPrimaryStudio(studioName);
      setStatusMessage(`✅ Primary studio set: ${studioName} for ${selectedDancer.name}`);
    } catch (error) {
      console.error('Error setting primary studio:', error);
      setStatusMessage('❌ Error setting primary studio');
    }
  };

  const recordTransfer = async () => {
    if (!selectedDancer || !newTransfer.fromStudio || !newTransfer.toStudio || !newTransfer.transferDate) {
      setStatusMessage('❌ Please fill in all required fields');
      return;
    }

    try {
      const transferDate = new Date(newTransfer.transferDate);
      const transferData = {
        id: `${selectedDancer.name}_${Date.now()}`, // Unique ID for reversion
        dancerName: selectedDancer.name,
        fromStudio: newTransfer.fromStudio,
        toStudio: newTransfer.toStudio,
        transferDate: transferDate,
        timestamp: Timestamp.now(),
        recordedBy: user?.email || 'Unknown',
        notes: newTransfer.notes || '',
        status: 'active' // For tracking active vs reverted transfers
      };

      // Save to transfers collection with unique ID
      const transferRef = doc(db, 'studioTransfers', transferData.id);
      await setDoc(transferRef, transferData);

      // Update dancer's profile with comprehensive transfer history
      await updateDancerProfileWithTransfer(selectedDancer.name, transferData);

      // Update competitions to reflect the transfer
      await updateCompetitionsWithTransfer(selectedDancer.name, transferData);

      setStatusMessage(`✅ Transfer recorded: ${selectedDancer.name} moved from ${newTransfer.fromStudio} to ${newTransfer.toStudio} on ${formatDate(transferDate)}`);
      
      // Reset form
      setNewTransfer({
        fromStudio: newTransfer.toStudio, // Set new "from" as previous "to"
        toStudio: '',
        transferDate: '',
        notes: ''
      });

      // Reload data
      await Promise.all([loadTransferHistory(), loadDancersWithMultipleStudios()]);
      
    } catch (error) {
      console.error('Error recording transfer:', error);
      setStatusMessage('❌ Error recording transfer');
    }
  };

  const updateDancerProfileWithTransfer = async (dancerName, transferData) => {
    try {
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      
      // Get existing profile
      const profileDoc = await getDocs(query(collection(db, 'dancerProfiles')));
      const existingProfile = profileDoc.docs.find(doc => doc.id === dancerName);
      const currentTransfers = existingProfile?.data()?.studioTransfers || [];
      
      // Add new transfer to history
      const updatedTransfers = [...currentTransfers, {
        id: transferData.id,
        type: 'studio_transfer',
        fromStudio: transferData.fromStudio,
        toStudio: transferData.toStudio,
        transferDate: transferData.transferDate,
        timestamp: transferData.timestamp,
        recordedBy: transferData.recordedBy,
        notes: transferData.notes,
        status: 'active'
      }];

      await setDoc(profileRef, {
        name: dancerName,
        studioTransfers: updatedTransfers,
        lastUpdated: Timestamp.now()
      }, { merge: true });

    } catch (error) {
      console.error('Error updating dancer profile:', error);
    }
  };

  const updateCompetitionsWithTransfer = async (dancerName, transferData) => {
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      const transferDate = transferData.transferDate;
      
      for (const competitionDoc of competitionsSnapshot.docs) {
        const competition = competitionDoc.data();
        let hasChanges = false;
        
        if (competition.results) {
          const updatedResults = competition.results.map(result => {
            // Find results for this dancer
            if (result.name === dancerName) {
              const competitionDate = new Date(competition.date);
              
              // If competition is after transfer date, update to new studio
              if (competitionDate >= transferDate && result.studio === transferData.fromStudio) {
                hasChanges = true;
                return {
                  ...result,
                  studio: transferData.toStudio,
                  originalStudio: result.studio, // Keep track of original
                  transferredFrom: transferData.fromStudio,
                  transferId: transferData.id // Link to transfer record
                };
              }
            }
            return result;
          });
          
          if (hasChanges) {
            await setDoc(competitionDoc.ref, {
              ...competition,
              results: updatedResults,
              lastUpdated: Timestamp.now(),
              transferUpdated: true
            });
          }
        }
      }
    } catch (error) {
      console.error('Error updating competitions with transfer:', error);
    }
  };

  const revertTransfer = async (transferId) => {
    try {
      // Get transfer data
      const transferDoc = await getDocs(query(collection(db, 'studioTransfers')));
      const transfer = transferDoc.docs.find(doc => doc.id === transferId);
      
      if (!transfer) {
        setStatusMessage('❌ Transfer not found');
        return;
      }
      
      const transferData = transfer.data();
      
      // Mark transfer as reverted
      await setDoc(transfer.ref, {
        ...transferData,
        status: 'reverted',
        revertedBy: user?.email || 'Unknown',
        revertedAt: Timestamp.now()
      });
      
      // Revert competitions
      await revertCompetitionTransfer(transferData);
      
      // Update dancer profile
      await revertDancerProfileTransfer(transferData.dancerName, transferId);
      
      setStatusMessage(`✅ Transfer reverted: ${transferData.dancerName} - ${transferData.fromStudio} → ${transferData.toStudio}`);
      
      // Reload data
      await Promise.all([loadTransferHistory(), loadDancersWithMultipleStudios()]);
      
    } catch (error) {
      console.error('Error reverting transfer:', error);
      setStatusMessage('❌ Error reverting transfer');
    }
  };

  const revertCompetitionTransfer = async (transferData) => {
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      
      for (const competitionDoc of competitionsSnapshot.docs) {
        const competition = competitionDoc.data();
        let hasChanges = false;
        
        if (competition.results) {
          const updatedResults = competition.results.map(result => {
            // Find results that were affected by this transfer
            if (result.name === transferData.dancerName && result.transferId === transferData.id) {
              hasChanges = true;
              return {
                ...result,
                studio: result.originalStudio || transferData.fromStudio,
                originalStudio: undefined,
                transferredFrom: undefined,
                transferId: undefined
              };
            }
            return result;
          });
          
          if (hasChanges) {
            await setDoc(competitionDoc.ref, {
              ...competition,
              results: updatedResults,
              lastUpdated: Timestamp.now()
            });
          }
        }
      }
    } catch (error) {
      console.error('Error reverting competition transfer:', error);
    }
  };

  const revertDancerProfileTransfer = async (dancerName, transferId) => {
    try {
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      const profileDoc = await getDocs(query(collection(db, 'dancerProfiles')));
      const existingProfile = profileDoc.docs.find(doc => doc.id === dancerName);
      
      if (existingProfile) {
        const currentTransfers = existingProfile.data()?.studioTransfers || [];
        const updatedTransfers = currentTransfers.map(transfer => 
          transfer.id === transferId 
            ? { ...transfer, status: 'reverted' }
            : transfer
        );
        
        await setDoc(profileRef, {
          ...existingProfile.data(),
          studioTransfers: updatedTransfers,
          lastUpdated: Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error reverting dancer profile transfer:', error);
    }
  };

  // Function to handle studio changes for specific competitions (now tracks changes instead of saving immediately)
  const handleCompetitionStudioChange = (entry, newStudio) => {
    const competitionKey = `${entry.competitionId}-${selectedDancer.name}`;
    const newPendingChanges = new Map(pendingChanges);
    
    if (newStudio === entry.studio) {
      // Remove change if reverting to original
      newPendingChanges.delete(competitionKey);
    } else {
      // Add or update pending change
      newPendingChanges.set(competitionKey, {
        entry,
        newStudio,
        originalStudio: entry.studio
      });
    }
    
    setPendingChanges(newPendingChanges);
    setHasUnsavedChanges(newPendingChanges.size > 0);
  };

  // Function to save all pending changes at once
  const saveAllChanges = async () => {
    if (!user) {
      setStatusMessage('❌ Please log in to save changes');
      return;
    }

    if (pendingChanges.size === 0) {
      setStatusMessage('❌ No changes to save');
      return;
    }

    try {
      setStatusMessage('🔄 Saving all changes...');
      
      // Group changes by competition ID for batch updates
      const competitionUpdates = new Map();
      
      for (const [key, change] of pendingChanges) {
        const { entry, newStudio } = change;
        const competitionId = entry.competitionId;
        
        if (!competitionUpdates.has(competitionId)) {
          const competitionDoc = competitionData.get(competitionId);
          if (competitionDoc) {
            competitionUpdates.set(competitionId, {
              doc: competitionDoc,
              updates: []
            });
          }
        }
        
        const competitionUpdate = competitionUpdates.get(competitionId);
        if (competitionUpdate) {
          competitionUpdate.updates.push({
            dancerName: selectedDancer.name,
            newStudio: newStudio || 'No Club',
            originalStudio: entry.studio
          });
        }
      }
      
      // Apply all updates to Firestore
      const updatePromises = [];
      
      for (const [competitionId, { doc: competitionDoc, updates }] of competitionUpdates) {
        const competitionRef = doc(db, 'competitions', competitionId);
        
        // Update the competition results for all dancers in this competition
        const updatedResults = competitionDoc.results.map(result => {
          const resultDancerName = result.name || result.dancerName;
          const update = updates.find(u => u.dancerName === resultDancerName);
          
          if (update) {
            return {
              ...result,
              studio: update.newStudio,
              previousStudio: update.originalStudio,
              editedBy: user.email,
              editedAt: Timestamp.now()
            };
          }
          return result;
        });

        updatePromises.push(
          updateDoc(competitionRef, {
            results: updatedResults,
            lastModified: Timestamp.now(),
            modifiedBy: user.email
          })
        );
      }
      
      // Execute all updates
      await Promise.all(updatePromises);
      
      // Clear pending changes
      setPendingChanges(new Map());
      setHasUnsavedChanges(false);
      
      setStatusMessage(`✅ Successfully saved ${pendingChanges.size} studio changes for ${selectedDancer.name}`);
      
      // Reload data to reflect changes
      await loadData();
      
      // Refresh the selected dancer data
      const updatedDancer = dancers.find(d => d.name === selectedDancer.name);
      if (updatedDancer) {
        selectDancer(updatedDancer);
      }

    } catch (error) {
      console.error('Error saving all changes:', error);
      setStatusMessage('❌ Error saving changes');
    }
  };

  // Function to discard all pending changes
  const discardAllChanges = () => {
    setPendingChanges(new Map());
    setHasUnsavedChanges(false);
    setStatusMessage('📝 All changes discarded');
  };

  // Function to apply bulk studio change to all competitions
  const applyBulkStudioChange = () => {
    if (!bulkChangeStudio) {
      setStatusMessage('❌ Please select a studio for bulk change');
      return;
    }

    if (!selectedDancer || !transferHistory.length) {
      setStatusMessage('❌ No dancer selected or no competition history');
      return;
    }

    // Create pending changes for all competitions
    const newPendingChanges = new Map();
    
    transferHistory.forEach((entry) => {
      const competitionKey = `${entry.competitionId}-${selectedDancer.name}`;
      
      // Only add if it's different from current studio
      if (bulkChangeStudio !== entry.studio) {
        newPendingChanges.set(competitionKey, {
          entry,
          newStudio: bulkChangeStudio,
          originalStudio: entry.studio
        });
      }
    });
    
    setPendingChanges(newPendingChanges);
    setHasUnsavedChanges(newPendingChanges.size > 0);
    setShowBulkChange(false);
    setBulkChangeStudio('');
    
    if (newPendingChanges.size > 0) {
      setStatusMessage(`📝 Bulk change applied: ${newPendingChanges.size} competitions will be changed to "${bulkChangeStudio}"`);
    } else {
      setStatusMessage('ℹ️ No changes needed - selected studio is already used in all competitions');
    }
  };

  const updateDancerProfile = async (dancerName, transferData) => {
    try {
      // Create or update dancer profile with transfer information
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      
      // Get existing profile or create new one
      const transferNote = {
        type: 'studio_transfer',
        fromStudio: transferData.fromStudio,
        toStudio: transferData.toStudio,
        transferDate: transferData.transferDate,
        timestamp: transferData.timestamp,
        recordedBy: transferData.recordedBy,
        notes: transferData.notes
      };

      // Update the profile with transfer history
      await setDoc(profileRef, {
        name: dancerName,
        studioTransfers: [transferNote], // This will be appended in a more complex implementation
        lastUpdated: Timestamp.now()
      }, { merge: true });

    } catch (error) {
      console.error('Error updating dancer profile:', error);
    }
  };

  const setStudioCollaboration = async (studios, disciplines) => {
    if (!selectedDancer || studios.length < 2) {
      setStatusMessage('❌ Please select at least 2 studios for collaboration');
      return;
    }

    try {
      const profileRef = doc(db, 'dancerProfiles', selectedDancer.name);
      await setDoc(profileRef, {
        name: selectedDancer.name,
        studioCollaboration: {
          studios: studios,
          disciplines: disciplines,
          setBy: user?.email || 'Unknown',
          timestamp: Timestamp.now(),
          isActive: true
        },
        lastUpdated: Timestamp.now()
      }, { merge: true });

      setStudioCollaborationState({ studios, disciplines, isActive: true });
      setShowCollaborationModal(false);
      setStatusMessage(`✅ Studio collaboration set: ${studios.join(' + ')} for ${selectedDancer.name}`);
    } catch (error) {
      console.error('Error setting studio collaboration:', error);
      setStatusMessage('❌ Error setting studio collaboration');
    }
  };

  const setDisciplineSpecificStudios = async (disciplineStudioMap) => {
    if (!selectedDancer || Object.keys(disciplineStudioMap).length === 0) {
      setStatusMessage('❌ Please set at least one discipline-studio mapping');
      return;
    }

    try {
      const profileRef = doc(db, 'dancerProfiles', selectedDancer.name);
      await setDoc(profileRef, {
        name: selectedDancer.name,
        disciplineStudios: disciplineStudioMap,
        disciplineStudiosSetBy: user?.email || 'Unknown',
        disciplineStudiosTimestamp: Timestamp.now(),
        lastUpdated: Timestamp.now()
      }, { merge: true });

      setDisciplineStudios(disciplineStudioMap);
      setShowDisciplineModal(false);
      setStatusMessage(`✅ Discipline-specific studios set for ${selectedDancer.name}`);
    } catch (error) {
      console.error('Error setting discipline-specific studios:', error);
      setStatusMessage('❌ Error setting discipline-specific studios');
    }
  };

  const filteredDancers = dancers.filter(dancer =>
    dancer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dancer.studios.some(studio => studio.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>🔄 Studio Transfers</h2>
        <p>Loading dancers with multiple studio representations...</p>
      </div>
    );
  }

  return (
    <div className="studio-transfers-container">
      <h2>🔄 Studio Transfers</h2>
      <p className="description">
        Track and manage dancers who have represented different studios throughout their competition history.
        This tool shows when dancers changed studios and allows you to record official transfer dates.
      </p>

      {statusMessage && (
        <div className={`status-message ${statusMessage.startsWith('❌') ? 'error' : 'success'}`}>
          {statusMessage}
        </div>
      )}

      <div className="search-section">
        <input
          type="text"
          placeholder="Search dancers or studios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <p>Found {filteredDancers.length} dancers with multiple studio representations</p>
      </div>

      <div className="transfers-layout">
        {/* Dancers List */}
        <div className="dancers-section">
          <h3>📋 Dancers with Multiple Studios</h3>
          <div className="dancers-list">
            {filteredDancers.map((dancer, index) => (
              <div 
                key={index}
                className={`dancer-card ${selectedDancer?.name === dancer.name ? 'selected' : ''}`}
                onClick={() => selectDancer(dancer)}
              >
                <div className="dancer-header">
                  <DancerThumbnail dancerName={dancer.name} size={40} showPlaceholder={true} />
                  <div className="dancer-info">
                    <h4>{dancer.name}</h4>
                    <p>{dancer.transferCount} transfer{dancer.transferCount > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="studios-list">
                  {dancer.studios.map((studio, idx) => (
                    <span key={idx} className="studio-badge">
                      {studio}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transfer Details */}
        {selectedDancer && (
          <div className="transfer-details">
            <h3>📈 Transfer History: {selectedDancer.name}</h3>
            
            {/* Competition History */}
            <div className="history-section">
              <div className="history-header">
                <h4>Competition History</h4>
                <div className="history-controls">
                  {hasUnsavedChanges && (
                    <div className="batch-save-controls">
                      <button 
                        onClick={saveAllChanges}
                        className="save-all-btn"
                        title={`Save ${pendingChanges.size} pending changes`}
                      >
                        💾 Save All Changes ({pendingChanges.size})
                      </button>
                      <button 
                        onClick={discardAllChanges}
                        className="discard-all-btn"
                        title="Discard all pending changes"
                      >
                        🗑️ Discard All
                      </button>
                    </div>
                  )}
                  <button 
                    onClick={() => setShowBulkChange(!showBulkChange)}
                    className="bulk-change-btn"
                    title="Apply one studio to all competitions"
                  >
                    🎯 Bulk Change
                  </button>
                </div>
              </div>
              
              {/* Bulk Change Controls */}
              {showBulkChange && (
                <div className="bulk-change-section">
                  <h5>🎯 Bulk Studio Change</h5>
                  <p>Select a studio to apply to ALL competitions for {selectedDancer.name}:</p>
                  <div className="bulk-change-controls">
                    <select 
                      value={bulkChangeStudio} 
                      onChange={(e) => setBulkChangeStudio(e.target.value)}
                      className="bulk-studio-select"
                    >
                      <option value="">Select a studio...</option>
                      <option value="">No Studio</option>
                      {selectedDancer.studios.map((studio, idx) => (
                        <option key={idx} value={studio}>{studio}</option>
                      ))}
                      {allStudios.filter(studio => !selectedDancer.studios.includes(studio)).map((studio, idx) => (
                        <option key={`other-${idx}`} value={studio}>{studio}</option>
                      ))}
                    </select>
                    <button 
                      onClick={applyBulkStudioChange}
                      className="apply-bulk-btn"
                      disabled={!bulkChangeStudio}
                    >
                      ⚡ Apply to All Competitions
                    </button>
                    <button 
                      onClick={() => {
                        setShowBulkChange(false);
                        setBulkChangeStudio('');
                      }}
                      className="cancel-bulk-btn"
                    >
                      ❌ Cancel
                    </button>
                  </div>
                  <div className="bulk-warning">
                    ⚠️ This will stage changes for all {transferHistory.length} competitions. You can review before saving.
                  </div>
                </div>
              )}
              <p className="section-description">
                View and edit the studio representation for each competition. 
                Make your edits and then click "Save All Changes" to apply them at once.
                {hasUnsavedChanges && (
                  <span className="unsaved-notice"> ⚠️ You have {pendingChanges.size} unsaved changes.</span>
                )}
              </p>
              <div className="timeline">
                {transferHistory.map((entry, index) => {
                  const competitionKey = `${entry.competitionId}-${selectedDancer.name}`;
                  const pendingChange = pendingChanges.get(competitionKey);
                  const pendingStudio = pendingChange ? pendingChange.newStudio : null;
                  
                  return (
                    <CompetitionHistoryEntry 
                      key={`${entry.competitionId}-${index}`}
                      entry={entry} 
                      dancerName={selectedDancer.name}
                      formatDate={formatDate}
                      allStudios={allStudios}
                      onStudioChange={handleCompetitionStudioChange}
                      pendingStudio={pendingStudio}
                      isEditing={editingCompetition === `${entry.competitionId}-${index}`}
                      onEditToggle={() => {
                        if (editingCompetition === `${entry.competitionId}-${index}`) {
                          setEditingCompetition(null);
                        } else {
                          setEditingCompetition(`${entry.competitionId}-${index}`);
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Primary Studio Selection */}
            <div className="primary-studio-section">
              <h4>🎯 Primary Studio Name</h4>
              <p className="section-description">
                Select the primary studio name to be displayed in all results for this dancer. 
                This will override various registration names but can be overridden by studio owner's claimed display name.
              </p>
              <div className="primary-studio-selection">
                <div className="current-primary">
                  {primaryStudio ? (
                    <div className="primary-badge">
                      <strong>Current Primary:</strong> {primaryStudio}
                    </div>
                  ) : (
                    <div className="no-primary">No primary studio set</div>
                  )}
                </div>
                <div className="studio-options">
                  {selectedDancer.studios.map((studio, idx) => (
                    <button
                      key={idx}
                      className={`studio-option-btn ${primaryStudio === studio ? 'selected' : ''}`}
                      onClick={() => setPrimaryStudioForDancer(studio)}
                    >
                      {studio}
                      {primaryStudio === studio && ' ✓'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Studio Collaboration */}
              <div className="studio-collaboration">
                <h4>🤝 Studio Collaboration</h4>
                <p>Set up collaboration between multiple studios for this dancer</p>
                <button 
                  className="collaboration-btn"
                  onClick={() => setShowCollaborationModal(true)}
                >
                  Set Studio Collaboration
                </button>
                {studioCollaboration.isActive && (
                  <div className="collaboration-display">
                    <strong>Active Collaboration:</strong> {studioCollaboration.studios.join(' + ')}
                    {studioCollaboration.disciplines && (
                      <div><em>Disciplines: {studioCollaboration.disciplines}</em></div>
                    )}
                  </div>
                )}
              </div>

              {/* Discipline-Specific Studios */}
              <div className="discipline-studios">
                <h4>🎭 Discipline-Specific Studios</h4>
                <p>Assign different studios for different dance disciplines</p>
                <button 
                  className="discipline-btn"
                  onClick={() => setShowDisciplineModal(true)}
                >
                  Set Discipline Studios
                </button>
                {Object.keys(disciplineStudios).length > 0 && (
                  <div className="discipline-display">
                    <strong>Current Mappings:</strong>
                    {Object.entries(disciplineStudios).map(([discipline, studio]) => (
                      <div key={discipline} className="discipline-mapping">
                        <strong>{discipline}:</strong> {studio}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Record New Transfer */}
            <div className="record-transfer">
              <h4>📝 Record Studio Transfer</h4>
              <div className="transfer-form">
                <div className="form-row">
                  <label>From Studio:</label>
                  <select
                    value={newTransfer.fromStudio}
                    onChange={(e) => setNewTransfer(prev => ({ ...prev, fromStudio: e.target.value }))}
                  >
                    <option value="">Select studio...</option>
                    {selectedDancer.studios.map((studio, idx) => (
                      <option key={idx} value={studio}>{studio}</option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label>To Studio:</label>
                  <div className="studio-search-container">
                    <input
                      type="text"
                      value={newTransfer.toStudio}
                      onChange={(e) => {
                        setNewTransfer(prev => ({ ...prev, toStudio: e.target.value }));
                        setStudioSearchTerm(e.target.value);
                        setShowStudioDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowStudioDropdown(true)}
                      onBlur={() => setTimeout(() => setShowStudioDropdown(false), 200)}
                      placeholder="Type to search studios..."
                      className="studio-search-input"
                    />
                    {showStudioDropdown && studioSearchTerm && (
                      <div className="studio-dropdown">
                        {allStudios
                          .filter(studio => 
                            studio.toLowerCase().includes(studioSearchTerm.toLowerCase()) &&
                            studio !== newTransfer.fromStudio
                          )
                          .slice(0, 10)
                          .map((studio, idx) => (
                            <div
                              key={idx}
                              className="studio-option"
                              onClick={() => {
                                setNewTransfer(prev => ({ ...prev, toStudio: studio }));
                                setShowStudioDropdown(false);
                                setStudioSearchTerm('');
                              }}
                            >
                              {studio}
                            </div>
                          ))
                        }
                        {allStudios.filter(studio => 
                          studio.toLowerCase().includes(studioSearchTerm.toLowerCase()) &&
                          studio !== newTransfer.fromStudio
                        ).length === 0 && (
                          <div className="studio-option new-studio">
                            Create new: "{studioSearchTerm}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <label>Transfer Date:</label>
                  <input
                    type="date"
                    value={newTransfer.transferDate}
                    onChange={(e) => setNewTransfer(prev => ({ ...prev, transferDate: e.target.value }))}
                  />
                </div>

                <div className="form-row">
                  <label>Notes (optional):</label>
                  <textarea
                    value={newTransfer.notes}
                    onChange={(e) => setNewTransfer(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes about the transfer..."
                    rows={3}
                  />
                </div>

                <button 
                  onClick={recordTransfer}
                  className="transfer-button"
                  disabled={!newTransfer.fromStudio || !newTransfer.toStudio || !newTransfer.transferDate}
                >
                  🔄 Record Transfer: {newTransfer.fromStudio} → {newTransfer.toStudio}
                  {newTransfer.transferDate && ` on ${formatDate(new Date(newTransfer.transferDate))}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Transfers */}
      {transfers.length > 0 && (
        <div className="recent-transfers">
          <h3>📜 Recent Transfers</h3>
          <div className="transfers-list">
            {transfers.slice(0, 20).map((transfer) => (
              <div key={transfer.id} className={`transfer-record ${transfer.status === 'reverted' ? 'reverted' : ''}`}>
                <div className="transfer-info">
                  <strong>{transfer.dancerName}</strong>
                  <span className="transfer-arrow">
                    {transfer.fromStudio} → {transfer.toStudio}
                  </span>
                  <small>{formatDate(transfer.transferDate.toDate())}</small>
                  <span className={`transfer-status ${transfer.status}`}>
                    {transfer.status === 'reverted' ? '🔄 Reverted' : '✅ Active'}
                  </span>
                </div>
                <div className="transfer-details">
                  <div className="transfer-meta">
                    <small>Recorded by: {transfer.recordedBy}</small>
                    <small>On: {formatDate(transfer.timestamp.toDate())}</small>
                    {transfer.status === 'reverted' && (
                      <small>Reverted by: {transfer.revertedBy} on {formatDate(transfer.revertedAt.toDate())}</small>
                    )}
                  </div>
                  {transfer.notes && (
                    <div className="transfer-notes">"{transfer.notes}"</div>
                  )}
                  {transfer.status === 'active' && (
                    <button 
                      className="revert-btn"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to revert the transfer of ${transfer.dancerName} from ${transfer.fromStudio} to ${transfer.toStudio}? This will restore all competition results to the original studio.`)) {
                          revertTransfer(transfer.id);
                        }
                      }}
                    >
                      🔄 Revert Transfer
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Studio Collaboration Modal */}
      {showCollaborationModal && (
        <CollaborationModal
          dancer={selectedDancer}
          studios={selectedDancer?.studios || []}
          onSave={setStudioCollaboration}
          onClose={() => setShowCollaborationModal(false)}
        />
      )}

      {/* Discipline Studios Modal */}
      {showDisciplineModal && (
        <DisciplineStudioModal
          dancer={selectedDancer}
          studios={selectedDancer?.studios || []}
          existingMappings={disciplineStudios}
          onSave={setDisciplineSpecificStudios}
          onClose={() => setShowDisciplineModal(false)}
        />
      )}
    </div>
  );
}
